<template>
  <div ref="containerRef" class="scroll-container" @scroll="handleScroll">
    <!-- Initial Loader (when first fetching data) -->
    <v-row v-if="isLoading">
      <ProgressLinearLoaderVue :isLoading="isLoading" />
    </v-row>

    <!-- List of AHU Cards -->
    <v-row>
      <v-col v-for="ahu in tempItems" :key="ahu.deviceId">
        <!-- Render AHUCard only for dynamic layouts -->
        <AHUCard
          v-if="getDeviceFieldMappings(ahu.deviceId)"
          :AHUInfo="
            mapValuesForDisplay(ahu, getDeviceFieldMappings(ahu.deviceId))
          "
          :layoutConfig="getDeviceLayoutConfig(ahu.deviceId)"
        />
        <!-- A device with readings but no field mappings has nothing to
             render with; say so instead of dropping it from the page. -->
        <v-card
          v-else
          variant="outlined"
          width="360"
          class="pa-4 unmapped-device"
          data-cy="unmapped-device"
        >
          <div class="text-subtitle-1 font-weight-bold text-primary">
            {{ ahu.friendlyName || ahu.deviceName }}
          </div>
          <div class="text-caption text-sonicSilver">
            {{ ahu.siteLocationName }}
          </div>
          <div class="text-body-2 text-primary mt-2">
            No field mappings are configured for this AHU yet, so its
            readings cannot be shown.
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Spinner when loading more items -->
    <v-row v-if="isFetchingMore" class="loader-container">
      <v-progress-circular indeterminate color="primary" />
    </v-row>
  </div>
</template>

<script setup lang="ts">
import {
  ref,
  computed,
  watch,
  defineAsyncComponent,
  nextTick,
  onMounted,
  onBeforeUnmount,
} from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import { searchByTermFactory } from '@/Core.Patterns/Factory';
import { sortAlphabetically } from '@/Core.Patterns/Strategy';

const AHUCard = defineAsyncComponent(
  () => import('@/Core.Service.Domain/Controls/AHUs/Components/AHUCard.vue')
);

import {
  IAHU,
  IAHUMapping,
  AHULayoutContract,
  DeviceLayoutConfig,
} from '@/Core.Service.Domain/Controls/Controls.Common/types/index.ts';
import API from '@/Core.Service.Domain/Controls/AHUs/API/index.ts';
import defaultAHULayout from '@/Core.Service.Domain/Controls/AHUs/API/AHU.json';
import ProgressLinearLoaderVue from '@/Core.UI.Domain/Components/ProgressLinearLoader.vue';

const AHUsList = ref<IAHU[]>([]);
const isLoading = ref(true);
const isFetchingMore = ref(false);

const emits = defineEmits(['update:AHUsList', 'update:isLoading']);
const store = useStore();
const defaultSite = computed(() => store.getters['session/defaultSite']);
const controlCurrentRoute = computed(
  () => store.getters['ControlsStore/controlCurrentRoute']
);
const route = useRoute();

const updateDeleteAHU = computed(() => {
  return store.getters['ControlsStore/AHUsStore/getDeleteAHU'];
});

const searchTerm = computed(
  () => store.getters['ControlsStore/getControlSearchTerm']
);

const filteredItems = ref<IAHU[]>([]);
const visibleItems = ref<IAHU[]>([]);
const containerRef = ref<HTMLElement | null>(null);

const chunkSize = 10;

const updateVisibleItems = async () => {
  if (visibleItems.value.length >= filteredItems.value.length) return;

  isFetchingMore.value = true;
  await new Promise((resolve) => setTimeout(resolve, 500));

  const nextBatch = filteredItems.value.slice(
    visibleItems.value.length,
    visibleItems.value.length + chunkSize
  );

  visibleItems.value = [...visibleItems.value, ...nextBatch];
  isFetchingMore.value = false;
};

const tempItems = computed(() => {
  return visibleItems.value.length ? visibleItems.value : filteredItems.value;
});

const handleScroll = () => {
  if (!containerRef.value || isFetchingMore.value) return;

  const { scrollTop, clientHeight, scrollHeight } = containerRef.value;

  if (scrollTop + clientHeight >= scrollHeight - 150) {
    updateVisibleItems();
  }
};

const selectedSiteControls = computed<IAHU[]>(() => {
  return store.getters['ControlsStore/selectedSiteControls'];
});

const interval = 120000;
const intervalId = ref<ReturnType<typeof setInterval> | null>(null);

onMounted(() => {
  mapDynamicFields();
  intervalId.value = setInterval(() => {
    loadAhus();
  }, interval);
});

onBeforeUnmount(() => {
  if (intervalId.value) {
    clearInterval(intervalId.value);
  }
});

// Load AHUs
const loadAhus = async () => {
  isLoading.value = true;
  emits('update:isLoading', isLoading.value);
  try {
    const ahusLatestReadings = await API.getAHUsBySiteId(defaultSite.value.id);

    const cachedAHUs = JSON.parse(
      localStorage.getItem('local-storage-ahus') || '[]'
    );

    const filteredAHUs = cachedAHUs.filter((ahu: IAHU) => {
      const latestAhu = ahusLatestReadings.find(
        (latestAhu: IAHU) => latestAhu.deviceId === ahu.deviceId
      );
      if (latestAhu) {
        return ahu.usageDateTime >= latestAhu.usageDateTime;
      }
      return false;
    });

    localStorage.setItem('local-storage-ahus', JSON.stringify(filteredAHUs));

    if (filteredAHUs.length > 0) {
      const mergedAHUs = ahusLatestReadings.map((ahu: IAHU) => {
        const cached = filteredAHUs.find(
          (f: IAHU) => f.deviceId === ahu.deviceId
        );
        return cached ? { ...ahu, ...cached } : ahu;
      });
      AHUsList.value = mergedAHUs;
    } else {
      AHUsList.value = ahusLatestReadings;
    }
    await nextTick();
    AHUsList.value = AHUsList.value.map((ahu) => {
      const isSelected = selectedSiteControls.value.find((site) => {
        return site.id === ahu.deviceId;
      });
      return { ...ahu, ...isSelected };
    });
    AHUsList.value = AHUsList.value.filter((ahu) => {
      return ahu.isIgnored === false;
    });

    filteredItems.value = [...AHUsList.value];
    visibleItems.value = [];
    emits('update:AHUsList', AHUsList.value.length);
  } catch (error) {
    return Promise.reject(error);
  } finally {
    isLoading.value = false;
    emits('update:isLoading', isLoading.value);
    filteredItems.value = [...AHUsList.value];
    visibleItems.value = [];
    updateVisibleItems();
  }
};

const mappings = ref<IAHUMapping[]>([]);

// Every card renders the bundled AHU.json layout: the API no longer serves
// per-site layouts (its /sites/{siteId}/layout endpoint was removed).
const siteLayout = defaultAHULayout as AHULayoutContract;

const mapDynamicFields = async () => {
  try {
    mappings.value = await API.getAHUMappings(defaultSite.value.id);
  } catch (mappingError) {
    console.error(
      'Failed to fetch mappings, using default layout:',
      mappingError
    );
    mappings.value = [];
  }
};

const mappingsByDevice = computed<Record<number, IAHUMapping['mappings']>>(
  () => {
    const map: Record<number, IAHUMapping['mappings']> = {};
    // Ensure mappings.value is an array before iterating
    const mappingsArray = Array.isArray(mappings.value) ? mappings.value : [];
    for (const m of mappingsArray) {
      if (m && m.deviceId && m.mappings) {
        map[m.deviceId] = m.mappings;
      }
    }
    return map;
  }
);

const getDeviceFieldMappings = (deviceId: number) => {
  return mappingsByDevice.value[deviceId] || null;
};

const getDeviceLayoutConfig = (deviceId: number): DeviceLayoutConfig => {
  const deviceMappings = getDeviceFieldMappings(deviceId);

  if (deviceMappings) {
    return {
      type: 'dynamic',
      layout: siteLayout,
      mappings: deviceMappings,
    };
  }

  return {
    type: 'mappings-only',
    mappings: deviceMappings,
  };
};

// Map values from latest readings to display values using mappings
const mapValuesForDisplay = (
  ahu: IAHU,
  deviceMappings: IAHUMapping['mappings'] | null
) => {
  if (!deviceMappings) return ahu;

  const mappedValues = { ...ahu };

  // Create a dynamic map of canonical names to actual property names in the AHU object
  // Generate mapping from canonical names to camelCase property names dynamically
  const canonicalToPropertyMap: Record<string, keyof IAHU> = {};

  deviceMappings.forEach((mapping) => {
    const canonicalName = mapping.canonicalName;
    // Convert PascalCase canonical name to camelCase property name
    const propertyName =
      canonicalName.charAt(0).toLowerCase() + canonicalName.slice(1);

    // Verify the property exists on IAHU interface
    if (propertyName in ahu) {
      canonicalToPropertyMap[canonicalName] = propertyName as keyof IAHU;
    }
  });

  // Map values based on canonical names and mapping configuration
  deviceMappings.forEach((mapping) => {
    const propertyName = canonicalToPropertyMap[mapping.canonicalName];
    if (propertyName && propertyName in ahu) {
      // Only include the field if it's not ignored
      if (!mapping.isIgnored) {
        const value = ahu[propertyName];
        if (value !== undefined) {
          Object.assign(mappedValues, { [propertyName]: value });
        }
      }
    }
  });

  return mappedValues;
};

watch(
  [selectedSiteControls, controlCurrentRoute],
  async ([newValue, currentRoute]) => {
    if (!newValue.length) {
      // Once the controls bar has finished loading this route and found no
      // AHUs for the site, settle the loading state so the parent can show
      // its empty banner instead of an endless progress bar.
      if (currentRoute === route.name) {
        AHUsList.value = [];
        filteredItems.value = [];
        visibleItems.value = [];
        isLoading.value = false;
        emits('update:isLoading', false);
        emits('update:AHUsList', 0);
      }
      return;
    }
    if (newValue.length) {
      await loadAhus();
      // Fire-and-forget mappings; don't block initial AHU rendering
      mapDynamicFields();
      updateVisibleItems();
      await nextTick();
      if (containerRef.value) {
        containerRef.value.addEventListener('scroll', handleScroll);
      }
    }
  },
  { immediate: true }
);

watch(searchTerm, () => {
  filteredItems.value = searchByTermFactory(
    searchTerm.value,
    ['deviceName', 'friendlyName', 'deviceId', 'siteLocationName'],
    AHUsList.value
  );
  visibleItems.value = [];
  updateVisibleItems();
});

watch(AHUsList, () => {
  filteredItems.value = sortAlphabetically(AHUsList.value, 'friendlyName');
  visibleItems.value = [];
  updateVisibleItems();
});

watch(defaultSite, () => {
  loadAhus();
  mapDynamicFields();
});

watch(updateDeleteAHU, () => {
  if (updateDeleteAHU.value) {
    store.commit('ControlsStore/AHUsStore/SET_AHU_UPDATE_DELETE', false);
    loadAhus();
  }
});
</script>

<style lang="scss" scoped>
.scroll-container {
  height: 100vh;
  width: 100%;
  overflow-y: auto;
  padding: 102px 50px;
  margin-left: -50px;
  margin-top: -100px;
  scrollbar-width: none;
}

.loader-container {
  display: flex;
  justify-content: center;
  margin-top: -20%;
}

.v-col {
  flex-grow: 0;
}
</style>
